import { fabric } from 'fabric';

import { getCanvasStateManager } from '~/UI/components/organisms/FabricCanvas/hooks/canvasStateManager';

/**
 * Canvas safety validation
 * Checks if canvas is properly initialized, has valid dimensions, and is not disposed
 */
export const isCanvasSafe = (canvas: fabric.Canvas | null): boolean => {
  try {
    if (!canvas) {
      return false;
    }

    const stateManager = getCanvasStateManager();

    return (
      typeof canvas.getWidth === 'function' &&
      typeof canvas.getHeight === 'function' &&
      typeof canvas.renderAll === 'function' &&
      typeof canvas.clear === 'function' &&
      typeof canvas.dispose === 'function' &&
      typeof canvas.getContext === 'function' &&
      canvas.getWidth() > 0 &&
      canvas.getHeight() > 0 &&
      canvas.getContext() !== null &&
      !stateManager.isCanvasDisposed(canvas) &&
      !(canvas as fabric.Canvas & { _disposed?: boolean })._disposed
    );
  } catch (error) {
    console.warn('Canvas safety check failed:', error);

    return false;
  }
};

/**
 * Validates canvas context for rendering operations
 */
export const isCanvasContextValid = (canvas: fabric.Canvas | null): boolean => {
  try {
    if (!canvas) {
      return false;
    }

    const context = canvas.getContext();

    return (
      context !== null &&
      !(canvas as fabric.Canvas & { _disposed?: boolean })._disposed
    );
  } catch (error) {
    console.warn('Canvas context validation failed:', error);

    return false;
  }
};

/**
 * Safe canvas operation wrapper
 */
export const withCanvasSafety = <T>(
  canvas: fabric.Canvas | null,
  operation: () => T,
  fallback: T
): T => {
  if (!isCanvasSafe(canvas)) {
    console.warn('Canvas not safe for operation, using fallback');

    return fallback;
  }

  try {
    return operation();
  } catch (error) {
    console.warn('Canvas operation failed:', error);

    return fallback;
  }
};
